/**
 * Điều khiển TOÀN TUYẾN.
 *
 * Nguyên tắc bắt buộc: KHÔNG bao giờ gộp kết quả thành một chữ "thành công".
 * Server bung lệnh ra từng tủ và trả về kết quả từng tủ — màn hình này phải bày
 * đủ bảng đó ra: tủ nào đã gửi, tủ nào offline, tủ nào lỗi. Người trực ca cần
 * biết chính xác tủ nào không nghe lệnh để còn đi kiểm tra.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2, CircleSlash, Power, PowerOff, RotateCcw, Send, SlidersHorizontal, WifiOff, XCircle,
} from 'lucide-react';
import { api, errorMessage } from '../lib/api';
import { qk, useTree } from '../lib/queries';
import {
  Badge, Button, Card, EmptyState, ErrorBox, Modal, SectionTitle, Spinner, StatusDot, useToast,
} from '../components/ui';
import type { RouteControlResult, TreeCabinet } from '../lib/types';
import { useAuth } from '../lib/auth';
import { BangKhoaTuyen, KhoaTheoTuyen } from '../components/Khoa';

/** Ý định thao tác cho từng ngõ ra */
type OutputAction = 'keep' | 'on' | 'off';

export default function RouteControlPage() {
  const [params, setParams] = useSearchParams();
  const tree = useTree();
  const toast = useToast();
  const qc = useQueryClient();

  const routeId = params.get('tuyen') ?? '';
  /*
   * ➕ `P-6` `B4` mục 3 — CỜ THEO CHÍNH TUYẾN ĐANG MỞ. Trang này trước đây được gác một lần ở
   *   `App.tsx` bằng hạng `MANAGER`; nay hạng không trả lời được câu *"tuyến NÀY"*, nên câu hỏi
   *   phải đi vào trong trang và hỏi lại mỗi khi người dùng đổi tuyến trên thanh chọn.
   */
  const { coCoTuyen } = useAuth();
  const qdTuyen = coCoTuyen('canControl', routeId || null);
  const duocDieuKhien = qdTuyen === 'cho-qua';
  const [actions, setActions] = useState<Record<number, OutputAction>>({ 1: 'keep', 2: 'keep', 3: 'keep' });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<RouteControlResult | null>(null);

  useEffect(() => { document.title = 'Điều khiển toàn tuyến — SDKD'; }, []);

  const route = useMemo(
    () => (tree.data ?? []).find((r) => r.id === routeId) ?? null,
    [tree.data, routeId]
  );

  // Đổi tuyến thì chọn lại toàn bộ tủ của tuyến đó và xoá kết quả cũ
  useEffect(() => {
    setSelected(new Set((route?.cabinets ?? []).map((c) => c.id)));
    setResult(null);
  }, [route?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const chosenOutputs = useMemo(
    () => [1, 2, 3]
      .filter((i) => actions[i] !== 'keep')
      .map((i) => ({ index: i, on: actions[i] === 'on' })),
    [actions]
  );

  const targets = useMemo(
    () => (route?.cabinets ?? []).filter((c) => selected.has(c.id)),
    [route, selected]
  );

  const send = useMutation({
    mutationFn: () =>
      api.post<RouteControlResult>(`/api/routes/${routeId}/control`, {
        outputs: chosenOutputs,
        // Gửi kèm danh sách tủ khi người dùng chỉ chọn một phần; bỏ trống = toàn tuyến
        cabinetIds: targets.length === (route?.cabinets.length ?? 0) ? undefined : targets.map((c) => c.id),
      }),
    onSuccess: (res) => {
      setResult(res);
      setConfirmOpen(false);
      qc.invalidateQueries({ queryKey: qk.tree });
      qc.invalidateQueries({ queryKey: qk.overview });
      if (res.sent === res.total) {
        toast.success(`Đã gửi lệnh tới ${res.sent}/${res.total} tủ`);
      } else {
        toast.warning(
          `Gửi được ${res.sent}/${res.total} tủ`,
          `${res.offline} tủ offline, ${res.failed} tủ lỗi — xem bảng kết quả bên dưới.`
        );
      }
    },
    onError: (e) => {
      setConfirmOpen(false);
      toast.error('Không gửi được lệnh toàn tuyến', errorMessage(e));
    },
  });

  /*
   * ═════════════════════════════════════════════════════════════════════════════════════
   * 🔴 09/10/2026 — TRẢ QUYỀN TOÀN TUYẾN (ảnh H2 của lee).
   *
   * Lee bấm điều khiển theo tuyến, thoát ra, và mọi ngõ vẫn treo nhãn "Đang giữ bởi: ỨNG
   * DỤNG". Việc GIỮ là cố ý — một lệnh tay phải thắng lịch cho tới khi người ta trả lại.
   * Thiếu là ĐƯỜNG VỀ: trước bản này chỉ trả quyền được từng tủ một, trong trang chi tiết
   * tủ. Gửi một lệnh cho 20 tủ rồi phải mở 20 trang để trả lại — và mỗi tủ bỏ sót là một tủ
   * KHÔNG CÒN CHẠY LỊCH. Đó là P0, không phải một bất tiện.
   *
   * ⚠ Số tủ đang bị giữ đọc từ CÂY TUYẾN (`manualHold` do tủ khai), không phải từ việc app
   *   nhớ mình vừa gửi gì: app nhớ là app đoán, còn tủ khai là tủ đo.
   * ⚠ Nút chỉ hiện khi CÓ tủ đang bị giữ. Một nút luôn sáng là một nút không ai đọc nữa.
   * ═════════════════════════════════════════════════════════════════════════════════════
   */
  const tuDangGiu = useMemo(
    () => (route?.cabinets ?? []).filter((c) => c.outputs.some((o) => o.manualHold)),
    [route],
  );

  const traQuyen = useMutation({
    mutationFn: () => api.post<RouteControlResult>(`/api/routes/${routeId}/release`, {}),
    onSuccess: (res) => {
      setResult(res);
      qc.invalidateQueries({ queryKey: qk.tree });
      qc.invalidateQueries({ queryKey: qk.overview });
      /*
       * ⛔ "ĐÃ GỬI", KHÔNG PHẢI "ĐÃ TRẢ QUYỀN" — cùng luật đã ghi ở trang chi tiết tủ: ACK trả
       *   lời *"tủ đã NHẬN lệnh"*, không trả lời *"tủ đã TRẢ QUYỀN"*. Nhân chứng cho câu thứ
       *   hai là cột `manualHold` rụng về `false` ở khung tủ báo kế tiếp.
       */
      if (res.sent === res.total) toast.success(`Đã gửi lệnh trả quyền tới ${res.sent}/${res.total} tủ`);
      else toast.warning(
        `Gửi được ${res.sent}/${res.total} tủ`,
        `${res.offline} tủ offline, ${res.failed} tủ lỗi — xem bảng kết quả bên dưới.`,
      );
    },
    onError: (e) => toast.error('Không gửi được lệnh trả quyền toàn tuyến', errorMessage(e)),
  });

  const setAction = (index: number, value: OutputAction) =>
    setActions((prev) => ({ ...prev, [index]: value }));

  const toggleCabinet = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  /* ⛔ `duocDieuKhien` là vế ĐẦU: thiếu nó thì nút vẫn bấm được và người ta chỉ biết mình
     không có quyền sau khi lệnh đã đi tới máy chủ — và với một tuyến đèn thật, "biết sau" là
     một câu trả lời đắt. (Cửa thật vẫn ở máy chủ; đây là phép lịch sự.) */
  const canSend = duocDieuKhien && !!routeId && chosenOutputs.length > 0 && targets.length > 0;

  return (
    <div className="space-y-4">
      <SectionTitle>Điều khiển toàn tuyến</SectionTitle>

      {tree.isLoading && <Spinner label="Đang tải danh sách tuyến…" />}
      {tree.isError && <ErrorBox error={tree.error} onRetry={() => void tree.refetch()} />}

      {tree.data && (
        <Card title="1. Chọn tuyến đường">
          {tree.data.length === 0 ? (
            <EmptyState title="Chưa có tuyến đường nào" hint="Quản trị viên cần tạo tuyến trước." />
          ) : (
            <select
              className="inp max-w-md"
              value={routeId}
              onChange={(e) => {
                const v = e.target.value;
                if (v) setParams({ tuyen: v }); else setParams({});
              }}
            >
              <option value="">— Chọn tuyến đường —</option>
              {tree.data.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.tenDayDu} — {r.onlineCount}/{r.cabinetCount} tủ trực tuyến
                </option>
              ))}
            </select>
          )}
        </Card>
      )}

      {/*
        * 🔴 NÓI RA CA MÌNH ĐANG Ở, không chỉ tắt nút: bốn ca của `cauChanCoTuyen` dẫn tới bốn
        *   việc khác nhau (chọn tuyến khác · xin gán tuyến · xin cờ bật/tắt · tủ chưa có tuyến).
        */}
      {routeId && <BangKhoaTuyen qd={qdTuyen} co="canControl" />}
      {route && (
        <>
          <Card
            title="2. Chọn thao tác cho từng ngõ ra"
            goiY={{ nhan: 'Chọn thao tác', noiDung: 'Ngõ ra để “Giữ nguyên” sẽ không nằm trong lệnh gửi xuống tủ.' }}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-xl border border-vien p-3">
                  <p className="mb-2 text-sm font-semibold text-chu-1">Ngõ ra {i}</p>
                  <div className="grid grid-cols-3 gap-1">
                    <ActionButton
                      active={actions[i] === 'keep'}
                      onClick={() => setAction(i, 'keep')}
                      icon={<CircleSlash className="h-4 w-4" />}
                      label="Giữ nguyên"
                      tone="slate"
                    />
                    <ActionButton
                      active={actions[i] === 'on'}
                      onClick={() => setAction(i, 'on')}
                      icon={<Power className="h-4 w-4" />}
                      label="Bật"
                      tone="green"
                    />
                    <ActionButton
                      active={actions[i] === 'off'}
                      onClick={() => setAction(i, 'off')}
                      icon={<PowerOff className="h-4 w-4" />}
                      label="Tắt"
                      tone="red"
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card
            title="3. Chọn tủ áp dụng"
            subtitle={`${targets.length}/${route.cabinets.length} tủ được chọn`}
            actions={
              <>
                <Button size="sm" onClick={() => setSelected(new Set(route.cabinets.map((c) => c.id)))}>
                  Chọn tất cả
                </Button>
                <Button
                  size="sm"
                  onClick={() => setSelected(new Set(route.cabinets.filter((c) => c.status === 'ONLINE').map((c) => c.id)))}
                >
                  Chỉ tủ trực tuyến
                </Button>
                <Button size="sm" onClick={() => setSelected(new Set())}>Bỏ chọn hết</Button>
              </>
            }
          >
            {route.cabinets.length === 0 ? (
              <EmptyState title="Tuyến này chưa có tủ nào" />
            ) : (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {route.cabinets.map((c) => (
                  <CabinetPick
                    key={c.id}
                    cabinet={c}
                    checked={selected.has(c.id)}
                    onToggle={() => toggleCabinet(c.id)}
                  />
                ))}
              </div>
            )}
          </Card>

          <div className="sticky bottom-2 z-10">
            <div className="card flex flex-wrap items-center justify-between gap-3 p-3">
              <div className="text-sm text-chu-2">
                {chosenOutputs.length === 0 ? (
                  <span className="text-canh">Chưa chọn thao tác cho ngõ ra nào.</span>
                ) : (
                  <span>
                    Sẽ gửi:{' '}
                    <b>
                      {chosenOutputs.map((o) => `ngõ ${o.index} → ${o.on ? 'BẬT' : 'TẮT'}`).join(', ')}
                    </b>{' '}
                    cho <b>{targets.length}</b> tủ.
                  </span>
                )}
              </div>
              {/*
                * KHOÁ, không ẩn — và huy hiệu tắt vì băng đầu trang đã nói đủ.
                * ➕ `GD-09b` — BA CA, KHÔNG HAI: `KhoaTheoTuyen` nhận thẳng `qdTuyen`, nên ca
                *   `'chua-biet'` ra khung XÁM *"đang kiểm quyền"* thay vì khung hổ phách
                *   *"không có quyền"*. Bản cũ hỏi `duocDieuKhien` — một `boolean` — và một
                *   `boolean` không chở nổi ca thứ ba. Xem khối ở `KhoaTheoTuyen` (`Khoa.tsx`).
                */}
              <KhoaTheoTuyen qd={qdTuyen} co="canControl" hienHuyHieu={false}>
                <Button
                  variant="primary"
                  icon={<Send className="h-4 w-4" />}
                  disabled={!canSend}
                  onClick={() => setConfirmOpen(true)}
                >
                  Gửi lệnh toàn tuyến
                </Button>
              </KhoaTheoTuyen>
              {tuDangGiu.length > 0 && (
                <KhoaTheoTuyen qd={qdTuyen} co="canControl" hienHuyHieu={false}>
                  <Button
                    icon={<RotateCcw className="h-4 w-4" />}
                    loading={traQuyen.isPending}
                    onClick={() => traQuyen.mutate()}
                    title="Lịch tự động sẽ điều khiển lại các ngõ này"
                  >
                    Trả quyền về tự động ({tuDangGiu.length} tủ)
                  </Button>
                </KhoaTheoTuyen>
              )}
            </div>
          </div>
        </>
      )}

      {!route && !tree.isLoading && (
        <EmptyState
          icon={<SlidersHorizontal className="h-8 w-8" />}
          title="Chọn một tuyến đường để bắt đầu"
          hint="Thao tác này gửi lệnh tới nhiều tủ cùng lúc — hãy kiểm tra kỹ trước khi bấm gửi."
        />
      )}

      {result && <ResultTable result={result} />}

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Xác nhận điều khiển toàn tuyến"
        subtitle={route ? route.tenDayDu : ''}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)} disabled={send.isPending}>Huỷ</Button>
            <Button variant="primary" loading={send.isPending} onClick={() => send.mutate()} icon={<Send className="h-4 w-4" />}>
              Gửi ngay
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-chu-1">
          <p>
            {/* 🔴 Hộp thoại xác nhận là chỗ TUYỆT ĐỐI không được hiện tên trơ: giá của một
                cú bấm nhầm ở đây là cả một tuyến đường tối. */}
            Lệnh sẽ được gửi tới <b>{targets.length}</b> tủ của <b>{route?.tenDayDu}</b>:
          </p>
          <ul className="list-disc space-y-1 pl-5">
            {chosenOutputs.map((o) => (
              <li key={o.index}>
                Ngõ ra {o.index} → <b className={o.on ? 'text-tot' : 'text-chu-1'}>{o.on ? 'BẬT' : 'TẮT'}</b>
              </li>
            ))}
          </ul>
          {targets.some((c) => c.status !== 'ONLINE') && (
            <p className="rounded-lg bg-canh-nen p-2 text-xs text-canh">
              Trong danh sách có {targets.filter((c) => c.status !== 'ONLINE').length} tủ đang mất kết nối —
              những tủ này sẽ hiện “offline” trong bảng kết quả, không nhận được lệnh ngay.
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}

/* ------------------------------------------------------------ bộ phận con */

function ActionButton({
  active, onClick, icon, label, tone,
}: {
  active: boolean; onClick: () => void; icon: ReactNode; label: string;
  tone: 'slate' | 'green' | 'red';
}) {
  const activeCls = {
    slate: 'border-vien-manh bg-nen-2 text-chu-1',
    green: 'border-tot bg-tot/15 text-tot',
    red: 'border-nguy bg-nguy-nen text-nguy',
  }[tone];

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-xs font-medium transition',
        active ? activeCls : 'border-vien text-chu-2 hover:bg-nen-2',
      ].join(' ')}
    >
      {icon}
      {label}
    </button>
  );
}

function CabinetPick({
  cabinet, checked, onToggle,
}: { cabinet: TreeCabinet; checked: boolean; onToggle: () => void }) {
  const online = cabinet.status === 'ONLINE';
  const on = cabinet.outputs.filter((o) => o.isOn).length;

  return (
    <label
      className={[
        'flex cursor-pointer items-center gap-3 rounded-lg border p-2.5 transition',
        checked ? 'border-nhan/50 bg-nhan/10' : 'border-vien hover:bg-nen-2',
      ].join(' ')}
    >
      <input type="checkbox" checked={checked} onChange={onToggle} className="h-4 w-4 accent-nhan" />
      <StatusDot online={online} size="sm" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-chu-1">{cabinet.name}</span>
        <span className="block truncate text-xs text-chu-2">
          {cabinet.code} · {online ? 'trực tuyến' : 'mất kết nối'} · {on}/{cabinet.outputs.length} ngõ ra bật
        </span>
      </span>
    </label>
  );
}

function ResultTable({ result }: { result: RouteControlResult }) {
  return (
    <Card
      title="Kết quả gửi lệnh theo từng tủ"
      subtitle="Kết quả được liệt kê đầy đủ, không gộp — tủ nào không nhận được lệnh đều hiện rõ ở đây"
      actions={
        <div className="flex flex-wrap gap-1.5">
          <Badge tone="green"><CheckCircle2 className="h-3 w-3" />{result.sent} đã gửi</Badge>
          <Badge tone="amber"><WifiOff className="h-3 w-3" />{result.offline} offline</Badge>
          <Badge tone="red"><XCircle className="h-3 w-3" />{result.failed} lỗi</Badge>
          <Badge tone="slate">{result.total} tổng cộng</Badge>
        </div>
      }
    >
      <div className="tbl-wrap cuon-doc">
        <table className="tbl tbl-cuon">
          <thead>
            <tr>
              <th>Tủ</th>
              <th>Mã tủ</th>
              <th>Kết quả</th>
              <th>Chi tiết</th>
            </tr>
          </thead>
          <tbody>
            {/* Biến tên `kq` chứ không phải `r`: `r` ở tệp này là TUYẾN CHIẾU SÁNG, và một phép đo
                canh "không hiện tên đường trơ" không phân biệt được hai thứ trùng tên biến.
                Đổi tên biến là cách rẻ nhất để cái chốt đó nói đúng. */}
            {result.results.map((kq) => (
              <tr key={kq.cabinetId}>
                <td>
                  <Link to={`/tu/${kq.cabinetId}`} className="font-medium text-nhan hover:underline">
                    {kq.name}
                  </Link>
                </td>
                <td className="text-chu-2">{kq.code}</td>
                <td>
                  {kq.ok ? (
                    <Badge tone="green"><CheckCircle2 className="h-3 w-3" />Đã gửi xuống tủ</Badge>
                  ) : kq.state === 'SKIPPED_OFFLINE' ? (
                    <Badge tone="amber"><WifiOff className="h-3 w-3" />Tủ offline — chưa nhận</Badge>
                  ) : (
                    <Badge tone="red"><XCircle className="h-3 w-3" />Lỗi</Badge>
                  )}
                </td>
                <td className="text-xs text-chu-2">
                  {kq.error ?? (kq.commandId ? `Mã lệnh: ${kq.commandId.slice(0, 8)}…` : '—')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {result.offline > 0 && (
        <p className="mt-3 rounded-lg bg-canh-nen p-2 text-xs text-canh">
          {result.offline} tủ đang mất kết nối nên chưa nhận được lệnh. Khi tủ lên mạng trở lại,
          hãy kiểm tra và gửi lại lệnh nếu vẫn cần thay đổi trạng thái.
        </p>
      )}
    </Card>
  );
}
